<script setup lang="ts">
  import { ref, type Ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { useRouteQuery } from '@vueuse/router'
  import { useHead } from '@unhead/vue'
  import { useI18n } from 'vue-i18n'
  import apicore from '@/plugins/api'
  import { useNotify } from '@/composables/useNotify'
  import { useStepper } from '@vueuse/core'
  import { useUserStore } from '@/store/user'

  const router = useRouter()
  const redirect = useRouteQuery<string | null>('redirect')
  const { t } = useI18n()
  const notify = useNotify()

  const userStore = useUserStore()

  const { current, goTo } = useStepper([
    'ask-credentials',
    'verify-credentials'
  ])

  const loading = ref(false)
  const email = ref('')
  const password = ref('')
  const authError: Ref<false | string> = ref(false)

  function redirectToDeck() {
    // N'accepte que des chemins internes ('/...' mais pas '//' ni une URL
    // absolue) pour éviter un open redirect via le query param ?redirect=.
    const target = redirect.value
    const isInternalPath =
      typeof target === 'string' &&
      target.startsWith('/') &&
      !target.startsWith('//')
    router.push(isInternalPath ? { path: target } : { name: 'deck' })
  }

  async function handleLogin() {
    try {
      goTo('verify-credentials')
      loading.value = true
      authError.value = false

      const username = email.value.toLowerCase()

      await apicore.authenticate({
        username,
        password: password.value
      })
      userStore.fetchUserInfo()
      redirectToDeck()
    } catch (error) {
      goTo('ask-credentials')
      const message = error instanceof Error ? error.message : String(error)
      authError.value = message
      notify.error(message)
      loading.value = false
    }
  }

  useHead({
    title: `Login | ${import.meta.env.VITE_APP_TITLE}`
  })
</script>

<template>
  <form
    @submit.prevent="handleLogin"
    class="flex h-full items-center justify-center"
  >
    <fieldset
      class="fieldset bg-base-200 border-base-300 rounded-box w-xs border p-4"
    >
      <legend class="fieldset-legend">{{ t('auth.login') }}</legend>

      <label class="fieldset-label">{{ t('auth.email') }}</label>
      <input
        v-model.trim="email"
        :placeholder="t('auth.email')"
        :aria-label="t('auth.email')"
        :disabled="current !== 'ask-credentials'"
        :class="{
          'input-error': authError
        }"
        type="email"
        class="input validator"
        autocomplete="email"
        autofocus
        required
      />
      <div class="validator-hint hidden">{{ t('auth.email-warning') }}</div>

      <label class="fieldset-label">{{ t('auth.password') }}</label>
      <input
        v-model.trim="password"
        :placeholder="t('auth.password')"
        :aria-label="t('auth.password')"
        :disabled="current !== 'ask-credentials'"
        :class="{
          'input-error': authError
        }"
        type="password"
        class="input"
        autocomplete="password"
        required
      />

      <button
        aria-label="submit"
        class="btn btn-primary mt-4"
        type="submit"
        :disabled="loading"
      >
        <span
          v-if="loading"
          class="loading loading-spinner"
        ></span>
        {{ t('submit') }}
      </button>
    </fieldset>
  </form>
</template>
